import type { RiverGauge } from "../../../shared/outdoors";
import { cToF } from "../../../shared/weather";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { useRivers } from "../../data/weatherQueries";
import { EmptyState } from "../../ui/StatusBadge";
import { relativeAge, useNow } from "../../ui/time";
import { NeedsLocation, remPx, SkyFoot } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import "./outdoors.css";

type RiversConfig = Record<string, never>;

/** Streamflow at the USGS gauges nearest home (or the ones picked in Admin), with a 12-hour sparkline. */
function Rivers({ size, fullscreen, openFullscreen }: WidgetProps<RiversConfig>) {
  const { settings } = useHousehold();
  const now = useNow("minute");
  const q = useRivers();
  const state = dataStateOf(q, 60 * 60_000);
  if (!settings.location && settings.riverSites.length === 0)
    return <NeedsLocation icon="waves" title="Rivers need a home location" detail="Set a general location to see stream gauges nearby." compact={size.variant === "compact"} />;
  const data = q.data;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="waves" title="River gauges unavailable" detail={q.error?.message} />;
  const us = settings.temperatureUnit === "F";
  const compact = size.variant === "compact";
  // Whole rows only: a two-line gauge name with its details is about 3.4rem; header and footer take ~6.5rem.
  const rem = remPx();
  const fits = fullscreen ? data.gauges.length : Math.max(1, Math.floor((size.height - rem * (compact ? 3.5 : 6.5)) / (rem * 3.4)));

  return (
    <div className="outdoor rivers">
      <WidgetHeader title="Rivers & creeks" meta={settings.riverSites.length ? "Your gauges" : "Nearest gauges"} onTitle={fullscreen ? undefined : openFullscreen} />
      {data.gauges.length === 0 ? (
        <p className="muted">No stream gauges reporting near home.</p>
      ) : (
        <ul className="river-list">
          {data.gauges.slice(0, fits).map((g) => (
            <li key={g.siteId} className="river">
              <span className="river-main">
                <strong>{g.name}</strong>
                <span className="muted">
                  {[g.distanceKm !== null && `${us ? Math.round(g.distanceKm / 1.609) : Math.round(g.distanceKm)} ${us ? "mi" : "km"}`, g.waterTempC !== null && `water ${Math.round(us ? cToF(g.waterTempC) : g.waterTempC)}°`, g.observedAt && relativeAge(g.observedAt, now)]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              {!compact && <Spark values={g.history} trend={g.trend} />}
              <span className="river-flow">
                <b>{g.flowCfs === null ? "—" : g.flowCfs >= 100 ? Math.round(g.flowCfs).toLocaleString("en-US") : g.flowCfs.toFixed(g.flowCfs < 10 ? 1 : 0)}</b>
                <small>cfs {trendMark(g.trend)}</small>
              </span>
            </li>
          ))}
        </ul>
      )}
      {!compact && <SkyFoot source="USGS Water Services · provisional data" stamp={data.stamp} state={state} updatedAt={q.dataUpdatedAt} />}
    </div>
  );
}

const trendMark = (t: RiverGauge["trend"]) => (t === "rising" ? "▲" : t === "falling" ? "▼" : "");

function Spark({ values, trend }: { values: number[]; trend: RiverGauge["trend"] }) {
  if (values.length < 3) return <span className="river-spark" />;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const W = 80;
  const H = 26;
  const pts = values.map((v, i) => `${((i / (values.length - 1)) * W).toFixed(1)},${(H - 2 - ((v - lo) / Math.max(1e-6, hi - lo)) * (H - 4)).toFixed(1)}`).join(" ");
  return (
    <svg className={`river-spark trend-${trend ?? "steady"}`} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <polyline points={pts} />
    </svg>
  );
}

export const riversWidget: WidgetDefinition<RiversConfig> = {
  type: "rivers",
  version: 1,
  name: "Rivers & creeks",
  description: "Live streamflow at the USGS gauges nearest home, rising or falling, with water temperature where measured.",
  icon: "waves",
  category: "weather",
  defaultSize: { w: 4, h: 5 },
  minSize: { w: 3, h: 3 },
  stackHeight: 380,
  defaults: {},
  settings: [],
  provider: "usgs-water",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  Component: Rivers,
};
